// Standardized program framework content (clinical, honest, no social proof).
// Keyed by route slug. Consumed by <ProgramFramework slug="..." />.

export const programFramework = {
  'prediabetes-program': {
    label: 'Doctor-Led Prediabetes Program',
    problem:
      'Prediabetes is an early warning sign, not a diagnosis of diabetes, and it is the right time to act. Yet most people are simply told to "eat better and exercise" without a clear, personalized plan. A doctor-led approach uses your reports, medications and lifestyle to build practical changes that support healthier blood sugar.',
    who: [
      'Adults told they have prediabetes or borderline / high-normal HbA1c or fasting glucose',
      'People with a family history of type 2 diabetes or insulin resistance',
      'Anyone wanting to act early with a structured, medically guided plan',
    ],
    outcomes: [
      'A personalized nutrition, activity and lifestyle plan to support blood sugar management',
      'A focus on sustainable habits rather than extreme, short-lived diets',
      'Progress tracked and adjusted by your medical lead over time',
    ],
    scope: {
      inclusions: ['Health & lab review (HbA1c, glucose where available)', 'Personalized nutrition plan', 'Activity & lifestyle guidance', 'Regular medical check-ins'],
      exclusions: ['Prescription or medication changes', 'A guaranteed "reversal" of prediabetes', 'Replacement for your treating physician'],
      frequency: 'Regular follow-ups with scheduled medical review',
      channels: ['WhatsApp', 'Scheduled calls', 'Private plan delivery'],
      review: 'Plans adjusted at each follow-up based on readings, progress and how your body responds.',
    },
    responsibilities: [
      'Share accurate medical history, medications and recent reports',
      'Follow the agreed plan consistently and keep up monitoring',
      'Continue regular check-ups with your treating doctor',
    ],
  },

  'weight-management': {
    label: 'Doctor-Led Weight Management',
    problem:
      'Most weight-loss plans only count calories and ignore your medical history, medications, thyroid, insulin resistance and metabolism, which is why results often stall or do not last. A doctor-led approach addresses the factors a generic diet misses.',
    who: [
      'Adults who have struggled to lose weight despite genuine effort',
      'People with thyroid issues, PMOS, insulin resistance or prediabetes',
      'Anyone on medication who wants a safe, supervised plan',
    ],
    outcomes: [
      'A medically informed, sustainable nutrition and activity plan',
      'A focus on body composition and metabolic markers, not just the scale',
      'Habits reviewed and adjusted by your medical lead over time',
    ],
    scope: {
      inclusions: ['Health & lab review', 'Personalized calorie & nutrition plan', 'Activity and strength guidance', 'Regular medical check-ins'],
      exclusions: ['Weight-loss medication prescription', 'Guaranteed or rapid results', 'Replacement for your treating physician'],
      frequency: 'Regular follow-ups with scheduled medical review',
      channels: ['WhatsApp', 'Scheduled calls', 'Private plan delivery'],
      review: 'Plans adjusted at each follow-up based on progress, markers and how your body responds.',
    },
    responsibilities: [
      'Share accurate medical history, medications and recent reports',
      'Follow the agreed plan consistently and log progress',
      'Continue routine care with your treating doctor',
    ],
  },

  'doctor-led-fitness': {
    label: 'Doctor-Led Fitness & Nutrition',
    problem:
      'Generic fitness and diet plans ignore your medical history, medications, and lab markers, which can be ineffective or unsafe if you have an underlying condition. Doctor-led programs close that gap with medical oversight at every step.',
    who: [
      'Adults with conditions like diabetes, thyroid disorders, hypertension or obesity',
      'People on medication who need a safe, supervised plan',
      'Anyone who wants medical accountability rather than influencer advice',
    ],
    outcomes: [
      'A medically informed nutrition and training plan built around your reports',
      'Improved metabolic and fitness markers tracked over time',
      'Sustainable habits, reviewed and adjusted by your medical lead',
    ],
    scope: {
      inclusions: ['Health & lab review', 'Personalized nutrition plan', 'Structured training guidance', 'Regular medical check-ins'],
      exclusions: ['Diagnosis or prescription of medication', 'Emergency or acute medical care', 'Replacement for your treating physician'],
      frequency: 'Weekly check-ins with scheduled medical review',
      channels: ['WhatsApp', 'Scheduled calls', 'Private plan delivery'],
      review: 'Plans reviewed and adjusted at each follow-up based on progress and markers.',
    },
    responsibilities: [
      'Share accurate medical history, medications and recent reports',
      'Follow the agreed plan consistently and log progress',
      'Continue routine care with your treating doctor',
    ],
  },

  'fertility-nutrition': {
    label: 'Fertility Nutrition',
    problem:
      'Nutrition and lifestyle meaningfully influence reproductive health, yet most guidance is generic. A doctor-guided plan considers your hormonal profile and any underlying conditions to support healthier conditions for conception.',
    who: [
      'Individuals and couples planning conception',
      'People managing conditions such as PMOS (previously called PCOS) or thyroid disorders',
      'Those who want nutrition that complements fertility treatment',
    ],
    outcomes: [
      'A personalized, fertility-supportive nutrition and lifestyle plan',
      'Better blood sugar, weight and inflammatory markers where relevant',
      'Coordination that complements (not replaces) your fertility specialist',
    ],
    scope: {
      inclusions: ['Hormonal & lifestyle assessment', 'Fertility-supportive nutrition plan', 'Partner guidance where relevant', 'Ongoing follow-ups'],
      exclusions: ['Fertility diagnosis or treatment', 'Prescription of fertility medication', 'Replacement for your gynecologist/reproductive specialist'],
      frequency: 'Scheduled follow-ups with plan adjustments',
      channels: ['WhatsApp', 'Scheduled calls'],
      review: 'Plans adapted to your cycle, reports and treatment timeline.',
    },
    responsibilities: [
      'Continue care with your gynecologist / fertility specialist',
      'Share relevant reports and medications',
      'Commit to consistent nutrition and lifestyle changes',
    ],
  },

  'pregnancy-nutrition': {
    label: 'Pregnancy Nutrition',
    problem:
      'Nutritional needs change through every trimester, and unsafe advice can affect both mother and baby. Gynecologist-supervised plans keep nutrition safe, adequate and personalized.',
    who: [
      'Pregnant women seeking trimester-specific nutrition',
      'Those managing gestational concerns under medical supervision',
      'Mothers who want safe, evidence-based dietary guidance',
    ],
    outcomes: [
      'Trimester-specific, medically reviewed meal guidance',
      'Support for adequate maternal and fetal nutrition',
      'Confidence in safe food choices throughout pregnancy',
    ],
    scope: {
      inclusions: ['Trimester-specific plans', 'Supplement guidance (reviewed)', 'Safe food lists & alternatives', 'Regular check-ins'],
      exclusions: ['Antenatal medical care or scans', 'Diagnosis or treatment of pregnancy complications', 'Replacement for your obstetrician'],
      frequency: 'Regular check-ins adjusted to pregnancy progress',
      channels: ['WhatsApp', 'Scheduled calls'],
      review: 'Plans adjusted to symptoms, weight and trimester.',
    },
    responsibilities: [
      'Continue routine antenatal care with your obstetrician',
      'Report symptoms and follow medical advice',
      'Share supplements and any complications promptly',
    ],
  },

  'postpartum-recovery': {
    label: 'Postpartum Recovery',
    problem:
      'Postpartum recovery is often overlooked. Nutrition, gradual movement and recovery support help new mothers rebuild strength safely while caring for a newborn.',
    who: [
      'New mothers within the first year after delivery',
      'Those who are breastfeeding and need supportive nutrition',
      'Mothers easing back into fitness with medical oversight',
    ],
    outcomes: [
      'Recovery-focused, breastfeeding-friendly nutrition',
      'A gradual, safe return-to-fitness pathway',
      'Sustainable energy and strength rebuilding',
    ],
    scope: {
      inclusions: ['Recovery nutrition plan', 'Breastfeeding-friendly guidance', 'Gradual fitness reintroduction', 'Ongoing follow-ups'],
      exclusions: ['Postnatal medical care', 'Diagnosis/treatment of postpartum complications', 'Replacement for your doctor'],
      frequency: 'Scheduled follow-ups during recovery',
      channels: ['WhatsApp', 'Scheduled calls'],
      review: 'Plans adapted to recovery stage and energy levels.',
    },
    responsibilities: [
      'Get medical clearance before resuming exercise',
      'Share delivery type and any complications',
      'Prioritize rest and follow the agreed pace',
    ],
  },

  'online-fitness-coaching': {
    label: 'Online Fitness Coaching',
    problem:
      'Most online coaching focuses on workouts alone, with no medical safety net. Our coaching is delivered with medical oversight so progress is both effective and safe.',
    who: [
      'Anyone wanting structured fitness from home or gym',
      'People who want safe progression with medical supervision',
      'Busy individuals who need flexible, online coaching',
    ],
    outcomes: [
      'Customized workouts for your level and equipment',
      'Safe, progressive overload with medical oversight',
      'Improved strength, conditioning and consistency',
    ],
    scope: {
      inclusions: ['Customized workout plans', 'Technique & progression guidance', 'Weekly coach check-ins', 'Medical oversight'],
      exclusions: ['In-person training', 'Medical diagnosis or treatment', 'Physiotherapy / injury rehab'],
      frequency: 'Weekly check-ins (except Sundays)',
      channels: ['WhatsApp', 'Scheduled calls', 'Plan delivery'],
      review: 'Programs progressed based on performance and feedback.',
    },
    responsibilities: [
      'Disclose injuries or conditions before starting',
      'Follow technique guidance and train consistently',
      'Communicate pain or issues early',
    ],
  },

  'medical-nutrition-therapy': {
    label: 'Clinical Nutrition (MNT)',
    problem:
      'When a health condition requires dietary change, generic advice is not enough. Clinical Nutrition uses your diagnosis, labs and medications to design targeted, doctor-supervised nutrition.',
    who: [
      'People managing diabetes, thyroid, PMOS (previously called PCOS), hypertension or digestive issues',
      'Those on multiple medications needing safe nutrition',
      'Anyone whose doctor has recommended dietary changes',
    ],
    outcomes: [
      'Condition-specific, individualized nutrition therapy',
      'Improvement in relevant clinical markers over time',
      'Nutrition that works alongside your medical treatment',
    ],
    scope: {
      inclusions: ['Medical & lab assessment', 'Individualized nutrition therapy', 'Drug–nutrient consideration', 'Ongoing monitoring'],
      exclusions: ['Diagnosis or prescription', 'Acute / emergency care', 'Replacement for your treating physician'],
      frequency: 'Structured follow-ups with monitoring',
      channels: ['WhatsApp', 'Scheduled calls'],
      review: 'Therapy adjusted to labs, symptoms and response.',
    },
    responsibilities: [
      'Share diagnosis, labs and current medications',
      'Continue treatment with your treating doctor',
      'Follow the plan and report changes',
    ],
  },

  'pcos-diet-plan': {
    label: 'PMOS (previously called PCOS) Support',
    problem:
      'PMOS (previously called PCOS) is highly individual and often driven by insulin resistance. A doctor-guided, evidence-informed nutrition and lifestyle plan supports hormonal balance alongside your medical care.',
    who: [
      'People managing PMOS (previously called PCOS) symptoms',
      'Those with insulin resistance or irregular cycles',
      'Anyone wanting structured, medically responsible PMOS support',
    ],
    outcomes: [
      'A personalized, PMOS-aware nutrition and lifestyle plan',
      'Support for blood sugar, weight and hormonal balance',
      'Sustainable habits reviewed and adjusted over time',
    ],
    scope: {
      inclusions: ['PMOS-aware nutrition plan', 'Lifestyle & movement guidance', 'Hormone-supportive food guidance', 'Regular follow-ups'],
      exclusions: ['Diagnosis or prescription', 'Hormonal / fertility treatment', 'Replacement for your gynecologist'],
      frequency: 'Regular follow-ups with adjustments',
      channels: ['WhatsApp', 'Scheduled calls'],
      review: 'Plans adapted to symptoms, cycles and progress.',
    },
    responsibilities: [
      'Continue routine care with your physician/gynecologist',
      'Share reports and medications',
      'Commit to consistent nutrition and lifestyle changes',
    ],
  },
};

export default programFramework;
